import InfoIcon from "@mui/icons-material/InfoOutlined"; // Replace with your custom icon if needed
import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import BriaTooltip from "../../../../components/common/BriaTooltip/BriaTooltip";
import LoadingPlaceholder from "../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import RouterConstants from "../../../../constants/RouterConstants";
import { ORG_SUBSCRIPTION_PLANS } from "../../../../constants/billing";
import { RouteGroupKey } from "../../../../constants/routesMapper";
import { useApiLimitStatus } from "../../../../hooks/useApiLimitStatus";
import useSecureNavigate from "../../../../hooks/useSecureNavigate";
import { useAppStore } from "../../../../hooks/useStores";
import { OrgFeatures } from "../../../../models/billing";
import styles from "./UsageWidgetTG.module.scss";

type Props = {
	groupedUsageData: { [product in RouteGroupKey]: number };
};

const UsageWidgetTG = ({ groupedUsageData }: Props) => {
	const { t } = useTranslation("dashboard", { keyPrefix: "usage.tg" });
	const navigate = useSecureNavigate();
	const { authStore, tgStore, accountStore, pricingStore } = useAppStore();
	const { freeLimit } = useApiLimitStatus();
	const { tgModelStore } = tgStore;
	const trainedModels = tgModelStore.modelsList.filter((model) => model.status === "Completed").length;
	const activeModelsActions: Record<string, number> = {};
	const isPayingOrg = authStore.orgSubscription?.plan_name !== ORG_SUBSCRIPTION_PLANS.free.name;

	accountStore.allUsageData.forEach((day: Record<string, number>) => {
		Object.entries(day).forEach(([key, value]) => {
			if (key.startsWith("text-to-image/tailored/") && value > 0) {
				activeModelsActions[key] = (activeModelsActions[key] || 0) + value;
			}
		});
	});

	const activeModels = Object.keys(activeModelsActions);
	const totalActiveModelActions = Object.values(activeModelsActions).reduce((sum, val) => sum + val, 0);

	const total = Object.values(groupedUsageData).reduce((sum, val) => sum + val, 0);

	const handleTrainModelClick = () => navigate(RouterConstants.NEW_TG.PROJECTS.fullPath);

	const handleUpgradeClick = () => pricingStore.openPricingFlow();

	return (
		<Box className={styles.container}>
			<Box className={styles.title}>{t("title")}</Box>

			<Box className={styles.modelBox}>
				<Box className={styles.modelCountWrapper}>
					<LoadingPlaceholder isLoading={tgModelStore.loadingModelsList} size={20}>
						<Typography className={styles.modelCount}>{trainedModels}</Typography>
					</LoadingPlaceholder>
					<Typography className={styles.modelLabel}>{t("trainedModels")}</Typography>
				</Box>
				{(isPayingOrg || total < (authStore.orgUsage?.apiFreeCallsLimit ?? freeLimit)) && (
					<BriaButton
						buttonType="secondarySmall"
						className={styles.trainButton}
						onClick={handleTrainModelClick}
					>
						{t("trainModelButton")}
					</BriaButton>
				)}
			</Box>

			<Box className={styles.statsRow}>
				<Box className={styles.statBox}>
					<LoadingPlaceholder
						isLoading={accountStore.isLoadingAllUsageData || tgModelStore.loadingModelsList}
						size={20}
					>
						<Box className={styles.statCount}>{activeModels.length}</Box>
					</LoadingPlaceholder>
					<Box className={styles.statLabel}>
						{t("activeModels.label")}
						<BriaTooltip
							title={t("activeModels.info")}
							className={styles.info}
							componentsProps={{ tooltip: { className: styles.tooltip } }}
							displayOn="click"
						>
							<InfoIcon className={styles.infoIcon} />
						</BriaTooltip>
					</Box>
				</Box>

				<Box className={styles.statBox}>
					<LoadingPlaceholder
						isLoading={accountStore.isLoadingAllUsageData || tgModelStore.loadingModelsList}
						size={20}
					>
						<Box className={styles.statCount}>{totalActiveModelActions}</Box>
					</LoadingPlaceholder>

					<Box className={styles.statLabel}>
						{t("actionsWithActiveModels.label")}
						<BriaTooltip
							title={t("actionsWithActiveModels.info")}
							className={styles.info}
							componentsProps={{ tooltip: { className: styles.tooltip } }}
							displayOn="click"
						>
							<InfoIcon className={styles.infoIcon} />
						</BriaTooltip>
					</Box>
				</Box>
			</Box>

			{!authStore.isFeatureEnabled(OrgFeatures.TAILORED_GENERATION) && (
				<Box className={styles.upgradeRow}>
					<span>{t("upgradeMessage")}</span>
					<BriaButton
						buttonType="primaryMedium"
						className={styles.upgradeButton}
						onClick={handleUpgradeClick}
					>
						{t("upgradeButton")}
					</BriaButton>
				</Box>
			)}
		</Box>
	);
};

export default observer(UsageWidgetTG);
